import { createContext, useCallback, useContext, useMemo } from "react";
import { useLocation } from "wouter";
import { trackEvent } from "@/lib/analytics";

interface SignupDialogOptions {
  returnTo?: string;
}

interface SignupDialogContextType {
  isOpen: false;
  mode: "signup" | "login";
  openSignupDialog: (options?: SignupDialogOptions) => void;
  openLoginDialog: (options?: SignupDialogOptions) => void;
  closeSignupDialog: () => void;
}

const SignupDialogContext = createContext<SignupDialogContextType>({
  isOpen: false,
  mode: "signup",
  openSignupDialog: () => {},
  openLoginDialog: () => {},
  closeSignupDialog: () => {},
});

export function useSignupDialog() {
  return useContext(SignupDialogContext);
}

export function SignupDialogProvider({ children }: { children: React.ReactNode }) {
  const [, navigate] = useLocation();
  const openSignupDialog = useCallback((options?: SignupDialogOptions) => {
    trackEvent("signup_started");
    if (options?.returnTo) {
      navigate(`/sign-up?returnTo=${encodeURIComponent(options.returnTo)}`);
    } else {
      navigate("/sign-up");
    }
  }, [navigate]);
  const openLoginDialog = useCallback((options?: SignupDialogOptions) => {
    if (options?.returnTo) {
      navigate(`/sign-in?returnTo=${encodeURIComponent(options.returnTo)}`);
    } else {
      navigate("/sign-in");
    }
  }, [navigate]);
  // Intentional no-op: navigation-based auth flow has no dialog to close.
  // Retained for interface compatibility with legacy callers.
  const closeSignupDialog = useCallback(() => {}, []);
  const value = useMemo(() => ({
    isOpen: false as const,
    mode: "signup" as const,
    openSignupDialog,
    openLoginDialog,
    closeSignupDialog,
  }), [openSignupDialog, openLoginDialog, closeSignupDialog]);
  return (
    <SignupDialogContext.Provider value={value}>
      {children}
    </SignupDialogContext.Provider>
  );
}
